import { EventsFacade, type EventsCacheSlots } from "@tento-lona/sheets/platform";
import { Tracing } from "@tento-core/tracing";
/**
 * buildEventSubcolumn — produce a complete `CalendarSubcolumn` for
 * an event-shape row. Composes the three colocated helpers
 * (`buildEventSpans`, `buildAllDayEventsProvider` siblings) with
 * the layout container, create-event handler, and per-frame
 * loaders that the aggregator used to inline.
 *
 * The aggregator passes a small `Host` adapter that exposes the
 * host-specific data sources (events lookup, range loader,
 * filter), preferences, and the new-event UI hook. Everything
 * else is event-renderer-owned and lives here.
 *
 * Per permissive-renderer-architecture migration plan §Phase D
 * (step 3 — type-agnostic aggregator dispatch foundation).
 */

import { DateTime, DateUnit, Duration, type TimezoneRegion, type Utc } from "@tento-chrono";
import { Json, type Option, RowId } from "@tento-lona/sheets";
import { type EventItemPresentation, type SheetRow, type TimeFormat } from "@tento-lona";
import type { EventItemTimed } from "@tento-lona/sheets";
import {
  type CalendarSubcolumn,
  type CalendarSubcolumnSpan,
} from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import type { ItemActionContext } from "@tento-lona/sheets-ui/sheet/gestures/interactable-item.ts";
import { CalendarColumnLayoutGroup } from "@tento-ui/components/calendar/calendar-column-layout-group";
import { type EventItemDelegate } from "@tento-lona/sheets-ui/sheet/gestures/event-gesture-item.ts";
import { EventSpans } from "./build-event-spans.ts";
import { FrameMarks } from "@tento-ui/frame-marks";
import { EventTooltip } from "@tento-lona/sheets-ui/plugins/renderer/column-calendar/components/event-tooltip.ts";
import type { EventActiveState } from "./event-active-state.ts";
import type { CalendarReadValue } from "@tento-lona/sheets-ui/sheet/calendar-read-revision.ts";
import { liveVariantOf } from "../../../sheet/live-variant.ts";

/** Hook the host (today: CalendarListAliasPlugin) exposes for create-event UI. */
export interface NewEventActionsLike {
  show(timeRange: DateTime.Range<Utc>, context: ItemActionContext): void;
  showGhost(timeRange: DateTime.Range<Utc>): void;
  hide(): void;
  isRevealed(): boolean;
}

/**
 * Minimal surface the event subcolumn needs from its host. Built
 * per-row by the aggregator (CalendarListAliasPlugin) — the `filterEvent`
 * closure has the row's category/visibility config baked in.
 */
export interface EventSubcolumnHost {
  /** Host policy snapshot; omitted for hosts that cannot certify reuse. */
  readonly readIdentity?: Option<() => readonly CalendarReadValue[]>;
  /** Action delegate for per-event gesture commit (move/resize). */
  readonly baseDelegate: Option<EventItemDelegate>;
  /** Whether this event subcolumn should offer drag/click creation. */
  readonly canCreate: boolean;
  /** Optional opacity applied to rendered event cells in read-only contexts. */
  readonly readOnlyOpacity: Option<string>;
  /** Shared visual state for events with open details or context menus. */
  readonly eventActiveState: EventActiveState;
  /** The client's event categories and drafts (`client.events`): titles and colours. */
  readonly presentation: EventItemPresentation.Scope;
  /**
   * Events visible in a UTC range, before filter/category gating. `zone` is
   * the zone the caller's day columns are days of (the viewer's): a strip
   * bind's span over `bindRange` is read in it (sdk-to-dom-55).
   */
  partialEventsInRange(
    row: SheetRow.Joined.Handle,
    range: DateTime.Range<Utc>,
    cache: Option<EventsCacheSlots>,
    bindRange: Option<DateTime.Range<Utc>>,
    zone: Option<TimezoneRegion>,
  ): readonly EventItemTimed[];
  /**
   * Resolve once the recurring events `partialEventsInRange` would read for
   * `range` are expanded, so a synchronous read after it sees every
   * occurrence (`CalendarEventRead.prepareTimed`). Rejects with
   * `EventsFacade.SupersededError` when the row's events changed while
   * waiting, or with the expansion's error.
   */
  prepareEventsInRange(row: SheetRow.Joined.Handle, range: DateTime.Range<Utc>): Promise<void>;
  /** Category + calendar-visibility filter, configured per row by
   *  the host. Returning false drops the event from the column. */
  filterEvent(event: EventItemTimed): boolean;
  /** User preference accessor: how times are written. */
  timeFormat(): TimeFormat;
  /** Create-event UI hook. */
  newEventActions: NewEventActionsLike;
  /** Open the occluded-events modal. The host bundles `CalendarsClient`
   *  and modal-side preferences (date format, flight-info hook). */
  onShowOccluded(input: CalendarSubcolumn.ShowOccludedInput): void;
}

export interface EventSubcolumnInput {
  /** Constituent row whose events the subcolumn paints. */
  readonly row: SheetRow.Joined.Handle;
  readonly host: EventSubcolumnHost;
}

function buildEventSubcolumn(
  input: EventSubcolumnInput,
): CalendarSubcolumn {
  const { row, host } = input;
  const { baseDelegate, readIdentity } = host;
  const width = Json.string(row.variant?.attributes() ?? null, "width");
  const reads = new SharedFragmentRead((range, cache, bindRange, zone) => {
    const all = host.partialEventsInRange(row, range, cache, bindRange, zone);
    return { read: all.length, kept: all.filter((event) => host.filterEvent(event)) };
  });
  const visibleEvents = (
    range: DateTime.Range<Utc>,
    cache: Option<EventsCacheSlots>,
    bindRange: Option<DateTime.Range<Utc>>,
    zone: Option<TimezoneRegion>,
  ) => reads.visible(range, cache, bindRange, zone);
  /** A fragment's UTC range: `partialEventsInRange` filters by overlap. */
  const fragmentRange = (windowed: CalendarSubcolumn.SpanRenderContext["windowed"]) =>
    new DateTime.Range(windowed.fragment.start.toUtc(), windowed.fragment.end.toUtc());
  const makeSpansSync = (
    { windowed, tz: tzr, queryCache, bindRange }: CalendarSubcolumn.SpanRenderContext,
  ): CalendarSubcolumnSpan[] => {
    const utcRange = fragmentRange(windowed);
    const readStart = FrameMarks.begin();
    const { events, read } = visibleEvents(utcRange, queryCache ?? null, bindRange, tzr);
    FrameMarks.end("strip.read", readStart, read, events.length);
    const spansStart = FrameMarks.begin();
    const spans = EventSpans.build({
      row,
      events,
      windowed,
      tz: tzr,
      timeFormat: host.timeFormat(),
      baseDelegate,
      contentOpacity: host.readOnlyOpacity,
      eventActiveState: host.eventActiveState,
      presentation: host.presentation,
    });
    FrameMarks.end("strip.spans", spansStart, spans.length, 0);
    return spans;
  };
  return {
    ...(readIdentity ? {
      readIdentity: () => [
        row, liveVariantOf(row), host.baseDelegate, host.eventActiveState,
        host.canCreate, host.readOnlyOpacity, host.timeFormat(), host.newEventActions,
        ...readIdentity(),
      ],
    } : {}),
    rowId: RowId.fromLocalId(row.id),
    width: typeof width === "string"
      ? { type: "preferred", width }
      : { type: "flex" },
    loadData: async (date) => {
      const [pd, tz] = date;
      const start = tz.toWallClock(pd.start.withTime()).toUtc();
      const end = tz.toWallClock(pd.end.withTime()).toUtc();
      // ±90d padding holds typical month-or-two viewport scroll inside
      // the cached range, vs the fetcher's 28-day default which forces
      // a fresh batch POST every ~28d of scroll.
      await loadWithRecurrenceReadiness(
        async () => {
          await row.events().ensureLoaded(new DateTime.Range(start, end), DateUnit.days(90));
        },
        () => host.prepareEventsInRange(row, new DateTime.Range(start, end)),
        String(row.id),
        RECURRENCE_READINESS_BUDGET_MS,
      );
    },
    ...(host.canCreate ? {
      createHandler: {
          // Events snap to the 15-min grid; the shared create gesture reads
          // this and snaps click/drag start+end (and the ghost). Stickers omit
          // it for free placement.
          snapGranularity: Duration.Time.MIN15,
          onClickCreate: (position, context) => {
            const start = context.windowed.fragment.start.withTime(
              position.toTimeOfDayWrapping(),
            );
            const timeRange = new DateTime.Range(
              start,
              start.add(Duration.Time.hrs(1)),
            ).toUtc();
            // Bake `row` (Joined.Handle) into the gesture context.
            // Downstream actions resolve writes via the event's `origin`;
            // no separate EventsIndex parameter needed.
            host.newEventActions.show(timeRange, { ...context, row });
          },
          onDragCreate: (timeRange, context) => {
            host.newEventActions.show(timeRange, { ...context, row });
          },
          updateGhost: (timeRange) => {
            host.newEventActions.showGhost(timeRange);
          },
          clearGhost: () => {
            host.newEventActions.hide();
          },
          isCreateRevealed: () => host.newEventActions.isRevealed(),
          dismissCreate: () => host.newEventActions.hide(),
        },
    } : {}),
    makeLayout: () => CalendarColumnLayoutGroup.make(),
    makeSpansSync,
    // The rough boxes' read (`calendar-rough-layer`): the fragment's read,
    // kept for this bind's span read of the same fragment (one read per bind).
    roughSpans: ({ windowed, tz: tzr, queryCache, bindRange }) => {
      const readStart = FrameMarks.begin();
      const { events, read } = reads.readAhead(fragmentRange(windowed), queryCache ?? null, bindRange, tzr);
      FrameMarks.end("strip.read", readStart, read, events.length);
      return events.map((event) => ({ id: event.id, start: event.time.time.start, end: event.time.time.end }));
    },
    makeSpans: async (context): Promise<CalendarSubcolumnSpan[]> => makeSpansSync(context),
    occludedItems: (range, queryCache) => {
      const occlusionStart = FrameMarks.begin();
      const { events, read } = visibleEvents(range, queryCache, null, null);
      FrameMarks.end("strip.occlusion", occlusionStart, read, events.length);
      return events;
    },
    onShowOccluded: (input) => host.onShowOccluded(input),
    renderOccludedTooltip: (items) => EventTooltip.instance.bind([...items], host.presentation),
  };
}

/** The events a fragment read kept. */
type KeptEvents = readonly EventItemTimed[];

/** One provider read of a range: how many events it returned, and those the host's filter kept. */
export type FragmentRead = {
  readonly read: number;
  readonly kept: readonly EventItemTimed[];
};

/**
 * An event subcolumn's reads within one strip bind. The bind reads each day
 * fragment once: the span read also serves that fragment's occlusion
 * indicators, which ask for the same range with the same bind-owned cache
 * right after (calendar-days-strip.ts). Outside a bind (no cache) and for any
 * other range, every read stays live. Nothing is held past the synchronous
 * bind that made the read.
 *
 * Read-ahead (`calendar-rough-layer`): a strip's rough unit
 * reads every fragment before the day units that paint them, in an earlier
 * task. `readAhead` keeps each such read for the bind's cache until the span
 * read of the same fragment takes it (`visible`), so the bind still reads
 * each fragment once. It is held only while that cache object is: a new bind
 * or a moved read revision starts a new cache, and the kept reads go with
 * the old one.
 */
export class SharedFragmentRead {
  /** Reads kept ahead of their span read, per bind cache (`readAhead`); gone with the cache. */
  readonly #ahead: WeakMap<EventsCacheSlots, Map<string, KeptEvents>> = new WeakMap();
  #last: Option<{
    readonly cache: EventsCacheSlots;
    readonly startMs: number;
    readonly endMs: number;
    readonly kept: readonly EventItemTimed[];
  }> = null;

  constructor(
    readonly read: (
      range: DateTime.Range<Utc>,
      cache: Option<EventsCacheSlots>,
      bindRange: Option<DateTime.Range<Utc>>,
      zone: Option<TimezoneRegion>,
    ) => FragmentRead,
  ) {}

  /** Serve `range`'s occlusion read from this synchronous step only. */
  #remember(cache: EventsCacheSlots, range: DateTime.Range<Utc>, kept: readonly EventItemTimed[]): void {
    const last = { cache, startMs: range.start.mse, endMs: range.end.mse, kept };
    this.#last = last;
    // A strip bind (span read then occlusion read) is synchronous: release
    // the bind's slot map and events once it returns, never retain them.
    queueMicrotask(() => {
      if (this.#last === last) this.#last = null;
    });
  }

  /** The kept events of `range`, and how many the provider read for them (0 when served). */
  visible(
    range: DateTime.Range<Utc>,
    cache: Option<EventsCacheSlots>,
    bindRange: Option<DateTime.Range<Utc>>,
    zone: Option<TimezoneRegion>,
  ): { readonly events: readonly EventItemTimed[]; readonly read: number } {
    const ahead = cache == null ? null : this.#ahead.get(cache);
    const key = `${range.start.mse}..${range.end.mse}`;
    const readAhead = ahead?.get(key);
    if (cache != null && readAhead != null) {
      ahead?.delete(key);
      this.#remember(cache, range, readAhead);
      return { events: readAhead, read: 0 };
    }
    const last = this.#last;
    if (
      cache != null && last != null && last.cache === cache &&
      last.startMs === range.start.mse && last.endMs === range.end.mse
    ) return { events: last.kept, read: 0 };
    const { read, kept } = this.read(range, cache, bindRange, zone);
    if (cache == null) {
      this.#last = null;
    } else {
      this.#remember(cache, range, kept);
    }
    return { events: kept, read };
  }

  /**
   * Read `range` ahead of the bind step that paints it, and keep the read for
   * that step's `visible` of the same cache and range (taken once).
   */
  readAhead(
    range: DateTime.Range<Utc>,
    cache: Option<EventsCacheSlots>,
    bindRange: Option<DateTime.Range<Utc>>,
    zone: Option<TimezoneRegion>,
  ): { readonly events: readonly EventItemTimed[]; readonly read: number } {
    const result = this.visible(range, cache, bindRange, zone);
    if (cache != null) {
      let ahead = this.#ahead.get(cache);
      if (ahead == null) this.#ahead.set(cache, ahead = new Map());
      ahead.set(`${range.start.mse}..${range.end.mse}`, result.events);
    }
    return result;
  }
}

/**
 * A column load followed by recurrence readiness: the strip binds (and
 * certifies its read) after this resolves, so its synchronous read sees the
 * column's occurrences instead of kicking their expansion. Superseded
 * (`EventsFacade.SupersededError`): the rebuild's own invalidation rebinds, so
 * the load completes quietly. Failed: the read shows what is installed, as
 * before, the failure is reported, and the next bind's preparation retries.
 * A failed `load` still rejects.
 *
 * The wait for readiness is bounded by `budgetMs`: a slow or far-horizon
 * expansion (the facade joins the largest horizon any view asked for) never
 * keeps the column hidden. Past the budget the load resolves, the strip binds
 * what is installed now, and the expansion's own insert announcement
 * (`TimepointsChanged`, advancing the read revision) repaints it when it
 * lands; its outcome is still reported as above.
 */
export async function loadWithRecurrenceReadiness(
  load: () => Promise<void>,
  prepare: () => Promise<void>,
  rowId: string,
  budgetMs: number,
): Promise<void> {
  await load();
  const prepared = prepare().then(
    () => {},
    (error) => {
      if (!(error instanceof EventsFacade.SupersededError)) {
        Tracing.warn("ui")("calendar recurrence preparation failed", { rowId, error: String(error) });
      }
    },
  );
  let timer: Option<ReturnType<typeof setTimeout>> = null;
  const budget = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, budgetMs);
  });
  try {
    await Promise.race([prepared, budget]);
  } finally {
    if (timer != null) clearTimeout(timer);
  }
}

/**
 * How long a column waits for recurrence readiness before it binds what is
 * installed (`loadWithRecurrenceReadiness`). A warm horizon resolves on the
 * next microtask; this bounds only cold or far-horizon expansions.
 */
export const RECURRENCE_READINESS_BUDGET_MS = 100;

export namespace EventSubcolumn {
  export const build = buildEventSubcolumn;
}
